import { ProcedureModel } from '../types/Procedures';
import {
    CATEGORIES,
    CATEGORY_HUMANIZED,
    ProcedureCategory,
} from '../constants/procedure/CATEGORIES';
import { EntryModel, EntryProcedureToAdd } from '../types/Entries';
import { EntryProcedureModel } from '../types/EntryProcedure';
import { Identificator } from '../types/Identificator';
import Entry from '../db/services/Entry';
import { EntryDBServiceOptions } from '../types/dbService';

export type ProceduresGroup<
    T extends ProcedureModel | EntryProcedureToAdd = ProcedureModel,
> = {
    title: CATEGORY_HUMANIZED;
    procedures: T[];
};

const getProcedureCategory = (category: ProcedureCategory | undefined) => {
    if (!category) {
        return CATEGORY_HUMANIZED.other;
    }

    const isValidCategory = Boolean(CATEGORY_HUMANIZED[category]);

    return isValidCategory
        ? CATEGORY_HUMANIZED[category]
        : CATEGORY_HUMANIZED.other;
};

export function getGroupedProcedures<
    T extends ProcedureModel | EntryProcedureToAdd = ProcedureModel,
>(procedures: T[]): ProceduresGroup<T>[] {
    const groupedProcedures: {
        [key: string]: T[];
    } = {};
    CATEGORIES.forEach((category) => (groupedProcedures[category] = []));

    procedures.forEach((procedure) => {
        const procedureCategory = getProcedureCategory(procedure.category);
        groupedProcedures[procedureCategory].push(procedure);
    });

    return Object.keys(groupedProcedures).map((category) => ({
        title: category as CATEGORY_HUMANIZED,
        procedures: groupedProcedures[category],
    }));
}

export function mapWatermelonDBProceduresObjectsToRawObjects(
    entryProcedure: EntryProcedureModel,
): EntryProcedureToAdd {
    return {
        name: entryProcedure.name,
        price: entryProcedure.price,
        procedureId: entryProcedure.procedureId,
        quantity: entryProcedure.quantity,
    };
}

export const getProceduresFromEntries = async (entries: EntryModel[]) => {
    let procedures: EntryProcedureModel[] = [];

    for (let entry of entries) {
        const entryProcedures = await entry.entryProcedures.fetch();

        procedures = procedures.concat(entryProcedures);
    }

    return procedures;
};

export const getProceduresFromEntriesIds = async ({
    entriesIds,
    options,
}: {
    entriesIds: Identificator[];
    options: EntryDBServiceOptions;
}) => {
    const entryService = new Entry({
        database: options.database,
        imageService: options.imageService,
        logger: options.logger,
        validation: options.validation,
    });

    let procedures: EntryProcedureModel[] = [];

    for (let entryId of entriesIds) {
        const entry = await entryService.getByID(entryId.id);
        const entryProcedures = await entry.entryProcedures.fetch();

        procedures = procedures.concat(entryProcedures);
    }

    return procedures;
};
